import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { FormGroup, FormArray, FormControl, Validators } from '@angular/forms';
import { trigger, animate, style, transition } from '@angular/animations';

import { Subject } from 'rxjs';
import { map, filter, takeUntil } from 'rxjs/operators';

import { ThemeService } from '@library/common/branding';

import { AccountService } from '../account/account.service';

@Component({
   selector: 'app-pay',
   templateUrl: './pay.component.html',
   styleUrls: ['./pay.component.scss'],
   animations: [
      trigger( 'showX', [
         transition( ':enter', [
            style({ transform: 'translateX( 100% )' }),
            animate( 300 )
         ])
      ]),
      trigger( 'newLine', [
         transition( ':enter', [
            style({ height: 0, opacity: 0 }),
            animate( 300 )
         ]),
         transition( ':leave', [
            style({ height: '*' }),
            animate( 300, style({ height: 0, opacity: 0 }))
         ])
      ]),
      trigger( 'noteSwitch', [
         transition( ':enter', [
            style({ width: 0, height: 0 }),
            animate( 100, style({ height: '*' })),
            animate( 400, style({ width:  '*' }))
         ]),
         transition( ':leave', [
            style({ width: '*', height: '*' }),
            animate( 400, style({ width:  0 })),
            animate( 100, style({ height: 0 }))
         ])
      ]),
   ]
})
export class PayComponent implements OnInit, OnDestroy {
   loading = false;
   error: string;

   currency = 'USD';

   max = 100;
   total = 0;

   paymentForm: FormGroup = new FormGroup({
      account: new FormControl( ),
      override: new FormControl( ),
      notes: new FormControl({ value: '', disabled: true }),
      payments: new FormArray( [] ),
   });

   get account() { return this.paymentForm.get( 'account' ).value; }
   get override() { return this.paymentForm.get( 'override' ).value; }
   get notes() { return this.paymentForm.get( 'notes' ); }
   get payments() { return this.paymentForm.get( 'payments' ) as FormArray; }

   _onDestroy: Subject<void> = new Subject();

   constructor(
      public accountService: AccountService,
      private router: Router,
      public theme: ThemeService,
   ) {}

   ngOnInit() {
      this.paymentForm.get( 'account' ).setValue( this.accountService.account );
      this.paymentForm.get( 'override' ).setValue( this.account?.override );

      this.add();

      // magic
      this.payments.valueChanges.pipe(
         filter( payments => !!payments?.length ),
         takeUntil( this._onDestroy ),
      ).subscribe( payments => {
         const secondToLast = payments.slice( -2, -1 )[0];
         const last = payments.slice( -1 )[0];

         if ( secondToLast && !secondToLast?.id?.length && !secondToLast?.amount ) {
            this.payments.removeAt( this.payments.length - 1 );
         }

         if ( !!last?.id || !!last?.amount ) {
            this.add();
         }
      });

      this.payments.valueChanges.pipe(
         filter( value => !!value.length ),
         map( value => value.map( v => +v.amount ).reduce(( a, c ) => a + c, 0 )),
         takeUntil( this._onDestroy ),
      ).subscribe( total => this.total = total );
   }

   ngOnDestroy() {
      this.accountService.reset();
      this._onDestroy.next();
      this._onDestroy.complete();
   }

   add(): void {
      if ( this.payments.length > this.max ) { return; }

      const group = new FormGroup({
         id: new FormControl(),
         amount: new FormControl(),
      }, { validators: control => {
         const id = control.get( 'id' );
         const amount = control.get( 'amount' );

         if ( id.value || amount.value ) {
            if ( !id.validator ) {
               id.setValidators([ Validators.required, Validators.pattern( /^(\d{6,10}|[A-Za-z]\d{6})$/ )]);
               id.updateValueAndValidity();
               amount.markAsTouched();
            }

            if ( !amount.validator ) {
               amount.setValidators([ Validators.required, Validators.max( this.account?.prefs?.line_item_max ) ]);
               amount.updateValueAndValidity();
               id.markAsTouched();
            }
         } else {
            if ( id.validator ) {
               id.clearValidators();
               id.updateValueAndValidity();
            }

            if ( amount.validator ) {
               amount.clearValidators();
               amount.updateValueAndValidity();
            }
         }

         return null;
      }});

      this.payments.push( group );
   }

   pay(): void {
      const payload = this.paymentForm.value;
      payload.account = payload.account.number;
      payload.payments.splice( -1, 1 );

      this.router.navigate([ '/checkout' ], { state: { payload: { ...payload, total: this.total } } });
   }
}
